import { classNames } from "primereact/utils";
import { AnimatePresence, motion } from "framer-motion";
import styles from "./CalendarPanel.module.scss";
import { ICalendarDay } from "../models/employeeCalendarModels";
import { Icon, IconType } from "../../../../../icons/Icon";

interface ICalendarPanelProps {
  monthYearText: string;
  calendarDays: ICalendarDay[];
  canNavigatePrev: boolean;
  onNavigateMonth: (direction: "prev" | "next") => void;
  onDateSelect: (date: Date) => void;
}

const WEEKDAY_LABELS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Нд"];

const AVAILABILITY_TOOLTIP: Record<ICalendarDay["availability"], string> = {
  free: "Има свободни слотове",
  busy: "Напълно зает ден",
  closed: "Неработен ден",
};

export const CalendarPanel = ({
  monthYearText,
  calendarDays,
  canNavigatePrev,
  onNavigateMonth,
  onDateSelect,
}: ICalendarPanelProps) => {
  return (
    <div className={styles.panel}>
      <div className={styles.navigation}>
        <button
          type="button"
          className={classNames(styles.nav_button, {
            [styles.nav_button_disabled]: !canNavigatePrev,
          })}
          disabled={!canNavigatePrev}
          onClick={() => onNavigateMonth("prev")}
          aria-label="Предишен месец"
        >
          <Icon type={IconType.ArrowLeft} color="#fff" />
        </button>

        <AnimatePresence mode="wait">
          <motion.h3
            key={monthYearText}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.2 }}
            className={styles.month_title}
          >
            {monthYearText}
          </motion.h3>
        </AnimatePresence>

        <button
          type="button"
          className={styles.nav_button}
          onClick={() => onNavigateMonth("next")}
          aria-label="Следващ месец"
        >
          <Icon type={IconType.ArrowRight} color="#fff" />
        </button>
      </div>

      <div className={styles.weekday_header}>
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className={styles.weekday_label}>
            {label}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={monthYearText}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className={styles.grid}
        >
          {calendarDays.map((day, index) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const isPastDate = day.date < today;
            const isClickable = day.isCurrentMonth && !isPastDate;

            return (
              <button
                key={index}
                type="button"
                disabled={!isClickable}
                onClick={() => isClickable && onDateSelect(day.date)}
                className={classNames(styles.day, {
                  [styles.day_selected]: day.isSelected,
                  [styles.day_today]: day.isToday,
                  [styles.day_other_month]: !day.isCurrentMonth,
                  [styles.day_past]: isPastDate,
                  [styles.day_clickable]: isClickable,
                })}
                title={
                  day.isCurrentMonth && !isPastDate
                    ? AVAILABILITY_TOOLTIP[day.availability]
                    : undefined
                }
              >
                <span className={styles.day_number}>{day.dayNumber}</span>
                {day.isCurrentMonth && !isPastDate && (
                  <span
                    className={classNames(
                      styles.availability_dot,
                      styles[`availability_${day.availability}`]
                    )}
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
